import { Atom, useAtomValue } from 'jotai'

import { getConfig } from './config'

export class Api {
  _token: string

  constructor() {
    this._token = ''
  }

  setAuthToken(token: string) {
    this._token = token
  }

  clearAuthToken() {
    this._token = ''
  }

  fetch(input: string | URL | Request, init?: RequestInit) {
    const baseUrl = getConfig().apiUrl
    if (typeof input === 'string') {
      input = new URL(input, baseUrl)
    } else if (input instanceof URL) {
      input = new Request(input.toString(), init)
    } else {
      input = new Request(new URL(input.url, baseUrl).toString(), {
        ...input,
        ...init,
      })
    }

    return fetch(input, {
      ...init,
      headers: {
        // Default member token first; explicit Authorization (e.g. staff) wins.
        Authorization: this._token ? `Bearer ${this._token}` : '',
        ...(init?.headers || {}),
      },
    })
  }
}

export const api = new Api()

export type FieldError = Record<string, string>

export type Issues = (fieldName: string, issue: string) => boolean

/**
 * Normalize API error bodies into FieldError[].
 * Navaros returns: { error: "Validation error", fields: [{ password: "invalidCredentials" }] }
 * Some call sites also pass raw arrays or { Field, Error } shapes.
 */
export function normalizeFieldErrors(data: unknown): FieldError[] {
  if (!data) return []

  const toFieldError = (item: unknown): FieldError | null => {
    if (!item || typeof item !== 'object') return null
    const record = item as Record<string, unknown>
    if (typeof record['Field'] === 'string' && typeof record['Error'] === 'string') {
      return { [record['Field']]: record['Error'] }
    }
    if (typeof record['field'] === 'string' && typeof record['error'] === 'string') {
      return { [record['field']]: record['error'] }
    }
    // Already { password: "invalidCredentials" }
    const entries = Object.entries(record).filter(([, v]) => typeof v === 'string')
    if (entries.length > 0) return Object.fromEntries(entries) as FieldError
    return null
  }

  if (Array.isArray(data)) {
    return data.map(toFieldError).filter((e): e is FieldError => !!e)
  }

  if (typeof data === 'object') {
    const body = data as Record<string, unknown>
    if (Array.isArray(body['fields'])) {
      return body['fields'].map(toFieldError).filter((e): e is FieldError => !!e)
    }
  }

  return []
}

export function useFieldErrorsAtom(fieldErrorsAtom: Atom<FieldError[]>): Issues {
  const fieldErrors = useAtomValue(fieldErrorsAtom)
  const fieldErrorsMap = new Map<string, string>()
  for (const errorObj of fieldErrors) {
    for (const [field, error] of Object.entries(errorObj)) {
      fieldErrorsMap.set(field, error)
    }
  }

  return (field: string, error: string) => {
    return fieldErrorsMap.get(field) === error
  }
}
